import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Row, Col, Card, CardBody, CardHeader,
} from 'reactstrap';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import BaseGrid from '../../components/Common/BaseGrid';
import { Can } from '../../utilities/Can';
import { get, post } from '../../helpers/api_helper';
import SQLModal from '../../components/SQL/modal';
import SQLResultsModal from '../../components/SQL/ResultsModal';
import { renderQueryWithVariables } from '../../components/SQL/queryVariables';

function SQLPage(props) {
  const location = useLocation();
  const [signal, setSignal] = useState(0);
  const [shownForm, setShownForm] = useState(false);
  const [activeQuery, setActiveQuery] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [resumeResultsAfterEdit, setResumeResultsAfterEdit] = useState(false);
  const [pendingAutoRunId, setPendingAutoRunId] = useState(null);
  const sharedQueryId = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const runParam = params.get('run');

    return /^\d+$/.test(runParam || '') ? Number(runParam) : null;
  }, [location.search]);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  useEffect(() => {
    if (!sharedQueryId) {
      return undefined;
    }

    let cancelled = false;

    const loadSharedQuery = async () => {
      try {
        setResultsLoading(true);
        setResultsError(null);
        setResultsData(null);

        const sharedQuery = await get(`/api/sql/query/${sharedQueryId}`);

        if (cancelled) {
          return;
        }

        setActiveQuery(sharedQuery);
        setShownForm(false);
        setResultsOpen(true);
        setPendingAutoRunId(sharedQueryId);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setResultsOpen(true);
        setResultsError(error.response?.data || error.message);
      } finally {
        if (!cancelled) {
          setResultsLoading(false);
        }
      }
    };

    loadSharedQuery();

    return () => {
      cancelled = true;
    };
  }, [sharedQueryId]);

  const showCreate = () => {
    setActiveQuery({});
    setShownForm(true);
  };

  const showEdit = (query) => {
    setActiveQuery(query);
    setResumeResultsAfterEdit(false);
    setShownForm(true);
  };

  const editFromResults = () => {
    setResumeResultsAfterEdit(true);
    setResultsOpen(false);
    setShownForm(true);
  };

  const deleteQuery = async (query) => {
    try {
      await post(`/api/sql/query/${query.id}/delete`, {});
      setSignal(signal + 1);
      toastr.success(`Deleted SQL query ${query.name}`, 'Success');
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const runQuery = async (query) => {
    setActiveQuery(query);
    setResultsOpen(true);
    setResultsLoading(false);
    setResultsError(null);
    setResultsData(null);
    setPendingAutoRunId(null);
  };

  const buildShareUrl = (queryId) => (
    `${window.location.origin}${location.pathname}?run=${queryId}`
  );

  const copyShareLink = async (query) => {
    if (!query?.id) {
      return;
    }

    try {
      await navigator.clipboard.writeText(buildShareUrl(query.id));
      toastr.success(props.t('Share link copied'), props.t('Success'));
    } catch (clipboardError) {
      toastr.error(props.t('Unable to copy share link'), props.t('Error'));
    }
  };

  const executeQuery = async (renderedQuery) => {
    const queryToRun = activeQuery;

    if (!queryToRun?.id) {
      return;
    }

    try {
      setResultsLoading(true);
      setResultsError(null);
      setResultsData(null);

      const result = await post(`/api/sql/query/${queryToRun.id}/run`, {
        query: renderedQuery,
      });
      setResultsData(result);
    } catch (error) {
      setResultsError(error.response?.data || error.message);
    } finally {
      setResultsLoading(false);
    }
  };

  const handleAutoRun = async () => {
    if (!activeQuery?.id || pendingAutoRunId !== activeQuery.id) {
      return;
    }

    setPendingAutoRunId(null);
    await executeQuery(renderQueryWithVariables(activeQuery.query || ''));
  };

  const shareUrl = activeQuery?.id ? buildShareUrl(activeQuery.id) : '';

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col xxl="12">
              <Card>
                <CardHeader className="bg-transparent border-bottom">{props.t('SQL')}</CardHeader>
                <CardBody>
                  <Can i="see" this="SQL">
                    <BaseGrid
                      url="/api/sql"
                      hasSearch
                      create={showCreate}
                      createText="New SQL Query"
                      shouldUpdate={signal}
                      className="table table-sm table-hover table-striped table-bordered fs-6"
                      config={
                        {
                          columns: {
                            id: {
                              label: 'ID',
                              sort: true,
                              translate: false,
                              style: { fontSize: '10px' },
                            },
                            name: {
                              label: 'Name',
                              sort: true,
                              translate: false,
                            },
                            description: {
                              label: 'Description',
                              sort: false,
                              translate: false,
                              class: 'text-wrap',
                              style: { minWidth: '14rem' },
                            },
                            username: {
                              label: 'Username',
                              sort: true,
                              translate: false,
                            },
                            query: {
                              label: 'Query',
                              sort: false,
                              translate: false,
                              class: 'text-wrap',
                              format: (value) => {
                                if (!value) return '';
                                return value.length > 220 ? `${value.slice(0, 220)}...` : value;
                              },
                              style: {
                                minWidth: '24rem',
                                maxWidth: '24rem',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                              },
                            },
                            created_at: {
                              label: 'Created At',
                              date: {
                                format: (date) => (moment(date).fromNow()),
                              },
                              translate: false,
                            },
                            updated_at: {
                              label: 'Updated At',
                              date: {
                                format: (date) => (moment(date).fromNow()),
                              },
                              translate: false,
                            },
                            actions: {
                              label: 'Actions',
                              actions: [
                                {
                                  key: 'run',
                                  icon: 'mdi mdi-play',
                                  class: 'btn btn-sm btn-soft-primary',
                                  alt: props.t('Run'),
                                  click: runQuery,
                                },
                                {
                                  key: 'share',
                                  icon: 'mdi mdi-link-variant',
                                  class: 'btn btn-sm btn-soft-secondary',
                                  alt: props.t('Copy Share Link'),
                                  click: copyShareLink,
                                },
                                {
                                  key: 'edit',
                                  icon: 'mdi mdi-pencil',
                                  class: 'btn btn-sm btn-soft-info',
                                  alt: props.t('Edit'),
                                  click: showEdit,
                                },
                                {
                                  key: 'delete',
                                  icon: 'mdi mdi-delete',
                                  class: 'btn btn-sm btn-soft-danger',
                                  alt: props.t('Delete'),
                                  click: deleteQuery,
                                },
                              ],
                            },
                          },
                          pagination: {
                            sort: 'updated_at',
                            direction: 'desc',
                            perPage: 30,
                          },
                        }
                      }
                    />
                  </Can>

                  <Can not i="see" this="SQL"><p>{props.t('Unauthorized')}</p></Can>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <SQLModal
        shown={shownForm}
        query={activeQuery || {}}
        onSuccess={(savedQuery) => {
          setShownForm(false);
          setActiveQuery(savedQuery || activeQuery);
          setSignal(signal + 1);

          if (resumeResultsAfterEdit) {
            setResultsOpen(true);
            setResultsError(null);
            setResultsData(null);
            setResumeResultsAfterEdit(false);
          }
        }}
        onClose={() => {
          setShownForm(false);
          setResumeResultsAfterEdit(false);
        }}
      />

      <SQLResultsModal
        shown={resultsOpen}
        loading={resultsLoading}
        error={resultsError}
        result={resultsData}
        query={activeQuery}
        autoRunRequested={Boolean(activeQuery?.id && pendingAutoRunId === activeQuery.id)}
        shareUrl={shareUrl}
        onEdit={editFromResults}
        onAutoRun={handleAutoRun}
        onRun={executeQuery}
        onClose={() => setResultsOpen(false)}
      />
    </>
  );
}

SQLPage.defaultProps = {
  t: (x) => (x),
};

SQLPage.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(SQLPage));
